import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    CircularProgress
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import Dropzone from "react-dropzone";
import "./AddDocumentModal.css";

const FILE_MAXSIZE = 8497152;
const ACCEPTED_FILES = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";

@inject("documentsProfileStore", "templateStore")
@observer
class AddDocumentModal extends Component {
    state = {
        title: "",
        file: null,
        errors: {}
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.resetForm();
        }
    }

    resetForm = () => {
        this.setState({
            title: "",
            file: null,
            errors: {}
        });
    };

    handleTitleChange = (e) => {
        this.setState({ title: e.target.value });
    };

    handleFileDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            this.props.templateStore.openSnackbar("File couldn't be uploaded. Please check size (max 8MB) and format.");
            return;
        }
        if (acceptedFiles && acceptedFiles.length > 0) {
            this.setState({ file: acceptedFiles[0] });
        }
    };

    handleRemoveFile = () => {
        this.setState({ file: null });
    };

    validateForm = () => {
        const errors = {};
        const { title, file } = this.state;

        if (!title.trim()) {
            errors.title = "Document title is required";
        }
        if (!file) {
            errors.file = "Please select a file to upload";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async () => {
        if (!this.validateForm()) {
            return;
        }

        const { title, file } = this.state;

        const result = await this.props.documentsProfileStore.uploadDocument(
            title,
            "general",
            file,
            {}
        );

        if (result.success) {
            this.props.templateStore.openSnackbar("Document uploaded successfully");
            this.props.onClose();
        } else {
            this.props.templateStore.openSnackbar("Failed to upload document. Please try again.");
        }
    };

    getFileIcon(fileName) {
        if (!fileName) return "insert_drive_file";
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return "picture_as_pdf";
            case 'doc':
            case 'docx':
                return "description";
            default:
                return "insert_drive_file";
        }
    }

    formatFileSize(bytes) {
        if (!bytes) return "";
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    }

    render() {
        const { open, onClose } = this.props;
        const { documentUploadInProgress } = this.props.documentsProfileStore;
        const { title, file, errors } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                className="add-document-modal"
            >
                <DialogTitle disableTypography className="modal-title">
                    <div className="title-content">
                        <CloudUploadIcon className="title-icon" />
                        <span>Add Document</span>
                    </div>
                    <IconButton onClick={onClose} className="close-btn">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="modal-content">
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Document Title *"
                        placeholder="e.g., Working With Children Check, Tech Rider"
                        value={title}
                        onChange={this.handleTitleChange}
                        error={!!errors.title}
                        helperText={errors.title}
                    />

                    <div className="file-upload-section">
                        <Typography variant="body2" className="upload-label">
                            Upload File *
                        </Typography>
                        {!file ? (
                            <Dropzone
                                accept={ACCEPTED_FILES}
                                maxSize={FILE_MAXSIZE}
                                onDrop={this.handleFileDrop}
                                className={`dropzone ${errors.file ? 'error' : ''}`}
                            >
                                <CloudUploadIcon className="dropzone-icon" />
                                <h4>Drop your file here</h4>
                                <p>or click to browse</p>
                                <Typography variant="caption" color="textSecondary">
                                    PDF, DOC, DOCX, JPG, PNG (max 8MB)
                                </Typography>
                            </Dropzone>
                        ) : (
                            <div className="file-selected">
                                <InsertDriveFileIcon className="file-icon" />
                                <div className="file-info">
                                    <Typography variant="body1" className="file-name">
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {this.formatFileSize(file.size)}
                                    </Typography>
                                </div>
                                <IconButton onClick={this.handleRemoveFile} size="small" className="remove-file-btn">
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        )}
                        {errors.file && (
                            <Typography variant="caption" color="error" className="file-error">
                                {errors.file}
                            </Typography>
                        )}
                    </div>
                </DialogContent>

                <DialogActions className="modal-actions">
                    <Button onClick={onClose} disabled={documentUploadInProgress}>
                        CANCEL
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={documentUploadInProgress}
                    >
                        {documentUploadInProgress ? (
                            <>
                                <CircularProgress size={20} style={{ marginRight: 8 }} />
                                Uploading...
                            </>
                        ) : (
                            "UPLOAD DOCUMENT"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddDocumentModal;
